// src/belediye/redux/slices/sahaAssignmentsSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import sahaApi from '../../services/sahaApi';

/* ============================================================================
   Helpers (NEW)
   - Backward compatible: mevcut payload yapısı ne olursa olsun “atama zamanı”
     ve bazı alanları tek yerde normalize eder.
============================================================================ */

const pickFirst = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

const toIsoOrNull = (v) => {
  if (!v) return null;
  const d = new Date(v);
  // invalid date
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

/**
 * Payload farklı şekillerde gelebilir:
 * - AssignmentParticipantSerializer: { id, assignment, added_at, application_detail: {...}, ... }
 * - İleride zenginleşmiş: { assigned_at, assignment_detail: { assigned_at }, ... }
 *
 * Burada:
 * - assignmentAssignedAt: asıl “atama saati”
 * - participantAddedAt: personelin atamaya eklenme saati
 * - applicationCreatedAt: başvuru oluşturulma saati
 */
const computeDates = (item) => {
  const assignmentAssignedAt = pickFirst(
    item?.assigned_at,
    item?.assignment_assigned_at,
    item?.assignment_detail?.assigned_at,
    item?.assignment_detail?.assignedAt,
    item?.assignment?.assigned_at
  );

  const participantAddedAt = pickFirst(item?.added_at, item?.participant_added_at);

  const applicationCreatedAt = pickFirst(
    item?.application_detail?.created_at,
    item?.application_detail?.createdAt
  );

  return {
    assignmentAssignedAt: toIsoOrNull(assignmentAssignedAt),
    participantAddedAt: toIsoOrNull(participantAddedAt),
    applicationCreatedAt: toIsoOrNull(applicationCreatedAt),
  };
};

const normalizeAssignmentItem = (item) => {
  const dates = computeDates(item);

  // NEW: sadece ek metadata; mevcut alanları bozmaz
  const normalized = {
    ...item,
    _meta: {
      ...(item?._meta || {}),
      dates,
    },
  };

  // NEW LOG: atama saati yoksa uyar
  if (!dates.assignmentAssignedAt) {
    
  }

  return normalized;
};

const buildStats = (list) => {
  const stats = {
    total: list.length,
    active: 0,
    past: 0,
    byApplicationStatus: {}, // application_detail.status
  };

  for (const a of list) {
    if (a?.has_feedback) stats.past += 1;
    else stats.active += 1;

    const st = a?.application_detail?.status || '—';
    stats.byApplicationStatus[st] = (stats.byApplicationStatus[st] || 0) + 1;
  }

  return stats;
};

/* ============================================================================
   Thunks
============================================================================ */

/**
 * 1) Saha personelinin *tüm* participant görevlerini getir.
 * NEW: param destekler (mevcut çağrılar bozulmaz)
 */
export const fetchMyAssignments = createAsyncThunk(
  'sahaAssignments/fetchMyAssignments',
  async (params = {}, { rejectWithValue }) => {
    try {
      // NEW LOG
      

      // Not: JS ekstra argümanları tolere eder; sahaApi fetchMyAssignments imzası bozulmaz.
      const data = await sahaApi.fetchMyAssignments(params);

      // NEW LOG
      

      return data;
    } catch (err) {
      
      return rejectWithValue(err.message || 'Atamaları yüklerken hata oluştu.');
    }
  }
);

/**
 * 2) Tekil participant detayını getir (detay sayfası için).
 */
export const fetchAssignmentParticipantDetail = createAsyncThunk(
  'sahaAssignments/fetchAssignmentParticipantDetail',
  async (participantId, { rejectWithValue }) => {
    try {
      const data = await sahaApi.fetchParticipantById(participantId);

      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Detay yüklenemedi.');
    }
  }
);

/**
 * 3) Geri bildirim gönder.
 */
export const submitFeedback = createAsyncThunk(
  'sahaAssignments/submitFeedback',
  async ({ participant, status, note, files }, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;

    try {
      const feedback = await sahaApi.submitFeedback({ participant, status, note, files });

      

      //   Kritik: POST sonrası server tarafında oluşan file_urls vb. alanları almak için detail’ı yeniden çek
      // (POST response’unda file_urls yoksa bile GET’te gelir)
      dispatch(fetchAssignmentParticipantDetail(participant));

      return { participant, feedback };
    } catch (err) {
      return rejectWithValue(err.message || 'Geri bildirim gönderilemedi.');
    }
  }
);



/* ============================================================================
   State
============================================================================ */

const initialState = {
  // Liste (kartlar)
  assignments: [],
  loading: false,
  error: null,

  // Filtre (legacy tab)
  filter: 'all',

  // NEW: gelişmiş filtre/sıralama (UI isterse kullanır; kullanmazsa etkisiz)
  listQuery: {
    q: '', // arama
    sort: 'assigned_desc', // assigned_desc | assigned_asc | created_desc | created_asc
    dateField: 'assigned', // assigned | application | participant
    dateFrom: null, // ISO string
    dateTo: null, // ISO string
    applicationStatus: 'all', // yeni | işleme_alındı | ... | all
  },

  // NEW: özet istatistik (dashboard üst paneli için)
  stats: {
    total: 0,
    active: 0,
    past: 0,
    byApplicationStatus: {},
  },

  // NEW: son fetch zamanı (refresh / debug için)
  lastFetchedAt: null,

  // Detay
  detail: null,
  detailLoading: false,
  detailError: null,

  // Feedback işlemi
  feedbackStatus: null,
  feedbackError: null,
};

const sahaAssignmentsSlice = createSlice({
  name: 'sahaAssignments',
  initialState,
  reducers: {
    setFilter(state, action) {
      state.filter = action.payload;
    },

    // NEW: gelişmiş filtre/sıralama setter’ları (mevcudu bozmaz)
    setListQuery(state, action) {
      state.listQuery.q = action.payload || '';
      
    },
    setListSort(state, action) {
      state.listQuery.sort = action.payload || 'assigned_desc';
      
    },
    setListDateField(state, action) {
      state.listQuery.dateField = action.payload || 'assigned';
      
    },
    setListDateRange(state, action) {
      const { dateFrom, dateTo } = action.payload || {};
      state.listQuery.dateFrom = dateFrom || null;
      state.listQuery.dateTo = dateTo || null;
      
    },
    setApplicationStatusFilter(state, action) {
      state.listQuery.applicationStatus = action.payload || 'all';
      
    },
    resetListQuery(state) {
      state.listQuery = { ...initialState.listQuery };
      
    },

    clearError(state) {
      state.error = null;
    },
    clearFeedbackStatus(state) {
      state.feedbackStatus = null;
      state.feedbackError = null;
    },
    clearDetail(state) {
      // Sadece detaylı sayfa kapandığında veya route değiştiğinde çağır!
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    // 1) GÖREV LİSTESİ
    builder
      .addCase(fetchMyAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;

        // NEW LOG
        
      })
      .addCase(fetchMyAssignments.fulfilled, (state, { payload }) => {
        state.loading = false;

        const rawList = Array.isArray(payload) ? payload : [];
        const normalizedList = rawList.map(normalizeAssignmentItem);

        state.assignments = normalizedList;

        // NEW: stats + lastFetchedAt
        state.stats = buildStats(normalizedList);
        state.lastFetchedAt = new Date().toISOString();

        // NEW LOG
       
      })
      .addCase(fetchMyAssignments.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      
      });

    // 2) DETAY (ASSIGNMENT PARTICIPANT)
    builder
      .addCase(fetchAssignmentParticipantDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
        // state.detail = null;

        // NEW LOG
        
      })
      .addCase(fetchAssignmentParticipantDetail.fulfilled, (state, { payload }) => {
        state.detailLoading = false;

        // Backend’den veri null, undefined ya da boş obje ise hata mesajı
        if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
          state.detail = null;
          state.detailError = 'Kayıt bulunamadı veya veri eksik!';
          
          return;
        }

        // NEW: normalize detail (atama zamanı vb.)
        state.detail = normalizeAssignmentItem(payload);
        state.detailError = null;

        // NEW LOG
       
      })
      .addCase(fetchAssignmentParticipantDetail.rejected, (state, { payload }) => {
        state.detailLoading = false;
        state.detail = null;
        state.detailError = payload;
        
      });

    // 3) FEEDBACK
    builder
      .addCase(submitFeedback.pending, (state) => {
        state.feedbackStatus = 'loading';
        state.feedbackError = null;

        // NEW LOG
        
      })
     .addCase(submitFeedback.fulfilled, (state, { payload }) => {
  state.feedbackStatus = 'succeeded';

  state.assignments = state.assignments.map((item) =>
    item.id === payload.participant ? { ...item, has_feedback: true } : item
  );
  state.stats = buildStats(state.assignments);

  //   En kritik: detail’ı backend’den gelen güncel veriyle değiştir
  if (payload?.refreshedDetail) {
    state.detail = normalizeAssignmentItem(payload.refreshedDetail);
  } else if (state.detail && state.detail.id === payload.participant) {
    state.detail.has_feedback = true;
  }

  
})  
      .addCase(submitFeedback.rejected, (state, { payload }) => {
        state.feedbackStatus = 'failed';
        state.feedbackError = payload;
        
      });
  },
});

export const {
  setFilter,
  clearError,
  clearFeedbackStatus,
  clearDetail,

  // NEW exports
  setListQuery,
  setListSort,
  setListDateField,
  setListDateRange,
  setApplicationStatusFilter,
  resetListQuery,
} = sahaAssignmentsSlice.actions;

export default sahaAssignmentsSlice.reducer;
